import { useState, ReactNode, Children, useEffect, KeyboardEvent } from 'react';
import { Formik, Form } from 'formik';
import ConfiguratorMetaobject from '../Interfaces/ConfiguratorMetaobject';
import ShapeInterface from '../Interfaces/ShapeInterface';
import PriceDisplay from './Displays/PriceDisplay';
import CaptureExternalModel from './Fields/CaptureExternalModel';
import CaptureExternalBrand from './Fields/CaptureExternalBrand';
import HeaderDisplay from './Displays/HeaderDisplay';
import ConfiguratorDisplay from './Displays/ConfiguratorDisplay';
import { setParamByName, translations, icons, metaobject, getSessionStorageKey } from '../Helpers/WindowHelpers';
import { sectionSettings } from '../Helpers/WindowHelpers';
import StartOverButton from "./Displays/StartOverButton";
import parse from 'html-react-parser';

interface FormBuilderProps {
  children: ReactNode;
  initialValues: Record<string, any>;
  shapes: ShapeInterface[];
  metaobject: ConfiguratorMetaobject,
  open: boolean,
  getFirstViableStep: () => Promise<number>,
  closeConfigurator: () => void,
  onSubmit: (values: any, bag: any) => Promise<void>;
}

const FormBuilder: React.FC<FormBuilderProps> = ({ children, initialValues, shapes, metaobject, open, closeConfigurator, onSubmit, getFirstViableStep }) => {
  const [stepNumber, setStepNumber] = useState<number>(0);
  const steps = Children.toArray(children);
  const [snapshot, setSnapshot] = useState<Record<string, any>>(initialValues);

  const [submitting, setSubmitting] = useState<boolean>(false);

  const step = steps[stepNumber] as React.ReactElement;
  const totalSteps = steps.length;
  const isLastStep = stepNumber === totalSteps - 1;
  const coverInput = document.querySelector('input.configurator_variant:checked') as HTMLInputElement;

  const saveSnapShot = (values: any) => {
    console.log('snapshotValues', values);
    setSnapshot(values);
    sessionStorage.setItem(getSessionStorageKey(), JSON.stringify(values));
  }

  useEffect(() => {
    getFirstViableStep().then(result => {
      setStepNumber(result >= 0 ? result : 0);
    }).catch(error => console.error(error))
  }, []);

  useEffect(() => {
    if (!open) return;
    //@ts-ignore
    const focusableElements = tcgFocusableElements(document.querySelector('.configurator-view'));
    focusableElements[0].focus();
  }, [open]);

  useEffect(() => {
    saveSnapShot(initialValues);
  }, [initialValues]);


  const scrollToTop = () =>
    document.querySelector('.configurator-view').scrollTo(0, 0);

  const updateStepNumber = (step: number) => {
    if (step < 0 || step >= totalSteps) return;
    setStepNumber(step);
    setParamByName('step', (step + 2).toString());
  }

  const next = (values: any) => {
    scrollToTop();
    saveSnapShot(values);
    setStepNumber(Math.min(stepNumber + 1, totalSteps - 1));
    setParamByName('step', (stepNumber + 3).toString());
  };

  const previous = (values: any) => {
    scrollToTop();
    saveSnapShot(values);
    setStepNumber(Math.max(stepNumber - 1, 0));
    setParamByName('step', (stepNumber + 1).toString());
  };

  const handleSubmit = async (values: any, bag: any) => {
    if (!isLastStep) {
      next(values);
      return;
    }
    setSubmitting(true);
    await onSubmit(values, bag);
  };

  return (
    <Formik
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validationSchema={//@ts-ignore
        step.props.validationSchema}
      enableReinitialize={true}
    >
      {formik => (
        <Form>
          <div
            className={`configurator-view flex-column ${open ? 'is-open' : ''}`}
            onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
              //@ts-ignore
              const focusableElements = tcgFocusableElements(event.currentTarget);
              if (event.key === 'Tab') {
                if (event.shiftKey) { // shift + tab back
                  if (document.activeElement == focusableElements[0] || !focusableElements.includes(document.activeElement)) {
                    focusableElements[focusableElements.length - 1].focus();
                    event.preventDefault();
                  }
                } else { // tab forward
                  if (document.activeElement == focusableElements[focusableElements.length - 1] || !focusableElements.includes(document.activeElement)) {
                    focusableElements[0].focus();
                    event.preventDefault();
                  }
                }
              }
            }}
          >
            <div className="configurator-content">
              <div className="configurator-content__step--inner">
                <div id="configurator-menu" className="configurator-steps">
                  <HeaderDisplay stepNumber={stepNumber} setStep={updateStepNumber} closeConfigurator={closeConfigurator} />
                </div>
                <div className="configurator-content__step--display">
                  <StartOverButton classes="lg-down--hide" />
                  <ConfiguratorDisplay />
                </div>
                <div className="configurator-content__step configurator-content__step--selection">
                  <div className="configurator-content__step--section">
                    <CaptureExternalBrand />
                    <CaptureExternalModel type={metaobject.type} />
                    {step}
                  </div>
                  <div className="configurator-content__continue-wrapper sticky-bottom">
                    <div className="configurator-content__continue bg-color--white p-20 flex-column">
                      <div className="d--flex align-items--center justify--space-between width--100">
                        <PriceDisplay shapes={shapes} />
                        <div className="d--flex gap-20 sm--gap-5 sm--flex-column-reverse">
                          {stepNumber > 0 && (
                            <button className="link align-self--center" onClick={() => previous(formik.values)} type="button">
                              {translations.back}
                            </button>
                          )}
                          <SubmitButton isLastStep={isLastStep} loading={formik.isSubmitting || submitting} disabled={formik.isSubmitting || submitting || !formik.values?.dimensionsValid} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

interface SubmitButtonInterface {
  isLastStep: boolean,
  disabled: boolean
  loading: boolean
}

const SubmitButton = ({ isLastStep, disabled, loading }: SubmitButtonInterface) => {
  return (<button className={`configurator-content__continue--btn button button--primary `} type="submit">
    {loading ? <div className="loader"></div> :
      <>
        {isLastStep ? sectionSettings.label_add_to_cart : sectionSettings.label_continue}
      </>
    }

  </button>)
}

export default FormBuilder;
